import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PackageOpen, SearchX } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { EmptyState, PillTabs, SearchField, SectionCard, TablePagination } from '../../../components/design-system/admin';
import { ProductImageTile } from '../../../components/design-system/common/ProductImageTile';
import { SizeLabel } from '../../../components/design-system/common/SizeLabel';
import { networkErrorMessage, type NetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import type { BrandDetail, ProductRow } from '../../../common/hooks/network-admin.types';
import type { NetworkProductStatus } from '../../../common/models/network.model';
import { ErrorNote, ProductStatusBadge } from './network-parts';

type StatusFilter = 'all' | NetworkProductStatus;
const SEARCH_DEBOUNCE_MS = 300;

/** One network product as a card: the picture first, then what it is and who carries it. */
function ProductCard({ product, busy, onToggle, onOpen }: { product: ProductRow; busy: boolean; onToggle: (published: boolean) => void; onOpen: () => void }) {
    const { t } = useTranslation();
    const published = product.status === 'published';
    return (
        <Card className="gap-0 overflow-hidden p-0" data-testid={`product-card-${product.uuid || product._id}`}>
            <ProductImageTile
                src={product.imageUrl}
                alt={product.name}
                fluid
                className="rounded-none border-0 border-b"
                emptyLabel={t('ADMIN_NETWORK.COMMON.NO_IMAGE')}
                openLabel={t('ADMIN_NETWORK.COMMON.ENLARGE')}
            />
            <div className="flex flex-1 flex-col gap-1 p-3">
                <button type="button" onClick={onOpen} className="truncate text-start text-sm font-medium hover:underline" title={product.name}>
                    {product.name}
                </button>
                <p className="truncate text-xs text-muted-foreground">
                    <SizeLabel format={product.format} />
                    {product.collectionName ? ` · ${product.collectionName}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.PRODUCTS.RETAILERS_N', { count: product.retailerCount ?? 0 })}</p>
                <div className="mt-auto flex items-center justify-between gap-2 pt-2">
                    <ProductStatusBadge status={product.status} />
                    <div className="flex items-center gap-1.5">
                        <Label htmlFor={`pub-${product._id}`} className="sr-only">
                            {published ? t('ADMIN_NETWORK.PRODUCTS.UNPUBLISH') : t('ADMIN_NETWORK.PRODUCTS.PUBLISH')}
                        </Label>
                        <Switch
                            id={`pub-${product._id}`}
                            checked={published}
                            disabled={busy}
                            onCheckedChange={(v) => onToggle(v)}
                            aria-label={published ? t('ADMIN_NETWORK.PRODUCTS.UNPUBLISH') : t('ADMIN_NETWORK.PRODUCTS.PUBLISH')}
                            data-testid="publish-toggle"
                        />
                    </div>
                </div>
            </div>
        </Card>
    );
}

/** The brand's products, filtered by collection and status, publishable one by one. */
export function BrandProductsGrid({ brandId, collections, api, reloadKey, onChanged }: { brandId: string; collections: BrandDetail['collections']; api: NetworkAdminApi; reloadKey: number; onChanged: () => void }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [collectionId, setCollectionId] = useState('');
    const [status, setStatus] = useState<StatusFilter>('all');
    const [search, setSearch] = useState('');
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(24);
    const [rows, setRows] = useState<ProductRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [toggling, setToggling] = useState<string[]>([]);
    const seq = useRef(0);

    useEffect(() => {
        const id = window.setTimeout(() => {
            setQ(search.trim());
            setPage(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(id);
    }, [search]);

    const load = useCallback(async () => {
        const mine = ++seq.current;
        setLoading(true);
        setError(null);
        try {
            const res = await api.products({
                brandId,
                collectionId: collectionId || undefined,
                status: status === 'all' ? undefined : status,
                q: q || undefined,
                page: page + 1,
                limit: pageSize,
            });
            if (mine !== seq.current) return;
            setRows(res.items);
            setTotal(res.total);
        } catch (e) {
            if (mine !== seq.current) return;
            setRows([]);
            setTotal(0);
            setError(networkErrorMessage(e) || t('ADMIN_SHELL.COMMON.LOAD_FAILED'));
        } finally {
            if (mine === seq.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, brandId, collectionId, status, q, page, pageSize]);

    useEffect(() => {
        load();
    }, [load, reloadKey]);

    const toggle = async (product: ProductRow, publish: boolean) => {
        const before = product.status;
        setToggling((ids) => [...ids, product._id]);
        setRows((rs) => rs.map((r) => (r._id === product._id ? { ...r, status: publish ? 'published' : 'draft' } : r)));
        try {
            const saved = publish ? await api.publishProduct(product._id) : await api.unpublishProduct(product._id);
            if (saved?.status) setRows((rs) => rs.map((r) => (r._id === product._id ? { ...r, status: saved.status } : r)));
            onChanged();
        } catch (e) {
            setRows((rs) => rs.map((r) => (r._id === product._id ? { ...r, status: before } : r)));
            toast.error(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setToggling((ids) => ids.filter((x) => x !== product._id));
        }
    };

    const filtered = !!(q || collectionId || status !== 'all');

    return (
        <SectionCard title={t('ADMIN_NETWORK.PRODUCTS.TITLE')} description={t('ADMIN_NETWORK.PRODUCTS.HINT', { count: total })} testId="brand-products">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <NativeSelect
                            className="w-full sm:w-56"
                            value={collectionId}
                            onChange={(e) => {
                                setCollectionId(e.target.value);
                                setPage(0);
                            }}
                            aria-label={t('ADMIN_NETWORK.FIELDS.COLLECTION')}
                        >
                            <NativeSelectOption value="">{t('ADMIN_NETWORK.PRODUCTS.ALL_COLLECTIONS')}</NativeSelectOption>
                            {collections.map((c) => (
                                <NativeSelectOption key={c._id} value={c._id}>{c.name}</NativeSelectOption>
                            ))}
                        </NativeSelect>
                        <PillTabs<StatusFilter>
                            value={status}
                            onChange={(v) => {
                                setStatus(v);
                                setPage(0);
                            }}
                            ariaLabel={t('ADMIN_NETWORK.FIELDS.STATUS')}
                            options={[
                                { value: 'all', label: t('ADMIN_SHELL.COMMON.ALL') },
                                { value: 'published', label: t('ADMIN_NETWORK.PRODUCT_STATUS.PUBLISHED') },
                                { value: 'draft', label: t('ADMIN_NETWORK.PRODUCT_STATUS.DRAFT') },
                            ]}
                        />
                    </div>
                    <SearchField value={search} onChange={setSearch} placeholder={t('ADMIN_NETWORK.PRODUCTS.SEARCH')} />
                </div>

                {loading && !rows.length ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                        {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="aspect-[3/4] w-full rounded-xl" />)}
                    </div>
                ) : error ? (
                    <ErrorNote message={error} onRetry={load} />
                ) : !rows.length ? (
                    filtered ? (
                        <EmptyState icon={<SearchX />} title={t('ADMIN_NETWORK.PRODUCTS.EMPTY_SEARCH_TITLE')} description={t('ADMIN_NETWORK.PRODUCTS.EMPTY_SEARCH_BODY')} />
                    ) : (
                        <EmptyState icon={<PackageOpen />} title={t('ADMIN_NETWORK.PRODUCTS.EMPTY_TITLE')} description={t('ADMIN_NETWORK.PRODUCTS.EMPTY_BODY')} />
                    )
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6" data-testid="products-grid">
                            {rows.map((p) => (
                                <ProductCard
                                    key={p._id}
                                    product={p}
                                    busy={toggling.includes(p._id)}
                                    onToggle={(v) => toggle(p, v)}
                                    onOpen={() => navigate(`/network/products/${p._id}`)}
                                />
                            ))}
                        </div>
                        <TablePagination
                            className="px-0"
                            page={page}
                            pageSize={pageSize}
                            total={total}
                            onPageChange={setPage}
                            onPageSizeChange={(s) => {
                                setPageSize(s);
                                setPage(0);
                            }}
                            pageSizes={[12, 24, 48]}
                        />
                    </>
                )}
            </div>
        </SectionCard>
    );
}
